import { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsMore from "highcharts/highcharts-more";
import HighchartsReact from "highcharts-react-official";
import { Empty, Typography } from "antd";
import type { GaugeScaleConfig, GaugeSegmentConfig, MetricOption } from "../../Processing/types";
import { SCALE_TYPE_LABELS } from "../../Processing/gaugeScaleTypes";
import { buildGaugeOptions } from "../../WidgetComponents/buildGaugeOptions";
import { PreviewDisclaimer } from "../shared/PreviewDisclaimer";

HighchartsMore(Highcharts);

const { Text, Title } = Typography;

/** A representative baseline used only to render the dial when the real scale
 * comes from the backend (everything except an explicit custom max). */
const MOCK_BASELINE = 80;

function derivePreviewBounds(scale: GaugeScaleConfig): { min: number; max: number; needle: number } {
  const min = scale.min;
  let max: number;
  if (scale.type === "custom") {
    max = scale.customMax ?? min + 100;
  } else if (scale.type === "baseline_multiplier") {
    max = MOCK_BASELINE * scale.multiplier;
  } else {
    max = min + 100;
  }
  if (max <= min) max = min + 100;
  const needle = min + 0.65 * (max - min);
  return { min, max, needle: Math.round(needle * 10) / 10 };
}

interface GaugePreviewPanelProps {
  title: string;
  selectedMetricId: string | null;
  metricAgg: "avg" | "sum" | "best";
  scale: GaugeScaleConfig;
  segmentConfig: GaugeSegmentConfig;
  metrics: MetricOption[];
}

export function GaugePreviewPanel({
  title,
  selectedMetricId,
  metricAgg,
  scale,
  segmentConfig,
  metrics,
}: GaugePreviewPanelProps) {
  const chartAreaRef = useRef<HTMLDivElement | null>(null);
  const [chartHeight, setChartHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const el = chartAreaRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const h = Math.floor(entry.contentRect.height);
      if (h > 0) setChartHeight(h);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const metricName = metrics.find((m) => m.id === selectedMetricId)?.name;
  const displayTitle = title || metricName || "Gauge Preview";
  const hasMetric = selectedMetricId != null;

  const options = useMemo<Highcharts.Options | null>(() => {
    if (!hasMetric) return null;
    const { min, max, needle } = derivePreviewBounds(scale);
    const base = buildGaugeOptions(
      { segment_config: segmentConfig, display: { title } },
      needle,
      min,
      max,
    );
    return {
      ...base,
      chart: {
        ...((base.chart as Record<string, unknown>) ?? {}),
        height: chartHeight ?? null,
        reflow: true,
        animation: false,
      },
      pane: {
        ...((base.pane as Record<string, unknown>) ?? {}),
        center: ["50%", "75%"],
        size: "100%",
      },
      exporting: { enabled: false },
    };
  }, [hasMetric, scale, segmentConfig, title, chartHeight]);

  const summaryParts = [
    `Scale: ${SCALE_TYPE_LABELS[scale.type]}`,
    `${metricAgg.toUpperCase()} aggregation`,
    `${segmentConfig.segments} segments`,
  ];

  return (
    <>
      <div className="awm-preview-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
          <Title level={5} style={{ margin: 0, flexShrink: 0 }}>
            {displayTitle}
          </Title>
          {hasMetric && <PreviewDisclaimer />}
        </div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {summaryParts.join(" · ")}
        </Text>

        <div className="awm-preview-chart-area" ref={chartAreaRef}>
          {hasMetric && options ? (
            <HighchartsReact
              highcharts={Highcharts}
              options={options}
              immutable
              containerProps={{ style: { width: "100%", height: "100%", minHeight: 0 } }}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Pick a metric to see a preview"
            />
          )}
        </div>
      </div>

      <Text
        type="secondary"
        style={{ display: "block", textAlign: "center", marginTop: 6, fontSize: 12 }}
      >
        {hasMetric
          ? "Preview uses sample values to show the dial, segments, and scale"
          : "Pick a metric to see a preview..."}
      </Text>
    </>
  );
}
